import { useState, useEffect, useCallback } from 'react';

const NOTIFICATION_SETTINGS_KEY = 'notificationSettings';
const LAST_NOTIFICATION_KEY = 'lastNotificationTime';

// Limites SAAQ (en minutes)
const LIMITS = {
  maxDriving: 13 * 60,      // 13h conduite max
  maxOnDuty: 14 * 60,       // 14h service max
  cycleWarningThreshold: 10 * 60, // Alerter quand < 10h restantes sur le cycle
  dailyWarningPercent: 0.9  // Alerter à 90% de la limite journalière
};

// Cooldown entre les notifications (en ms)
const NOTIFICATION_COOLDOWN = 30 * 60 * 1000; // 30 minutes

export const useNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [supported, setSupported] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  // Vérifier le support et la permission au chargement
  useEffect(() => {
    const checkSupport = async () => {
      const isSupported = 'Notification' in window && 'serviceWorker' in navigator && !window.__IS_NATIVE__;
      setSupported(isSupported);
      
      if (isSupported) {
        setPermission(Notification.permission);
        
        // Enregistrer le Service Worker
        try {
          const registration = await navigator.serviceWorker.register('/sw.js');
          setSwRegistration(registration);
          console.log('[Notifications] Service Worker registered');
        } catch (err) {
          console.error('[Notifications] SW registration failed:', err);
        }
      }
    };
    
    checkSupport();
  }, []);

  // Demander la permission
  const requestPermission = useCallback(async () => {
    if (!supported) {
      return { granted: false, reason: 'not_supported' };
    }
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return { granted: result === 'granted', permission: result };
    } catch (err) {
      console.error('[Notifications] Permission request failed:', err);
      return { granted: false, reason: 'error' };
    }
  }, [supported]);

  // Obtenir les préférences utilisateur
  const getSettings = useCallback(() => {
    try {
      const saved = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {}
    return {
      hoursLimit: true,
      cycleLimit: true,
      inspectionReminder: true,
      messages: true
    };
  }, []);

  // Vérifier si on peut envoyer une notification (cooldown)
  const canSendNotification = useCallback((type) => {
    try {
      const lastTimes = JSON.parse(localStorage.getItem(LAST_NOTIFICATION_KEY) || '{}');
      const lastTime = lastTimes[type];
      if (!lastTime) return true;
      return Date.now() - lastTime > NOTIFICATION_COOLDOWN;
    } catch (e) {
      return true;
    }
  }, []);

  // Marquer qu'une notification a été envoyée
  const markNotificationSent = useCallback((type) => {
    try {
      const lastTimes = JSON.parse(localStorage.getItem(LAST_NOTIFICATION_KEY) || '{}');
      lastTimes[type] = Date.now();
      localStorage.setItem(LAST_NOTIFICATION_KEY, JSON.stringify(lastTimes));
    } catch (e) {}
  }, []);

  // Envoyer une notification
  const sendNotification = useCallback(async (title, body, options = {}) => {
    if (permission !== 'granted') {
      console.log('[Notifications] Permission not granted');
      return false;
    }

    const type = options.type || 'general';
    
    // Vérifier le cooldown
    if (!canSendNotification(type)) {
      console.log('[Notifications] Cooldown active for type:', type);
      return false;
    }

    // Vérifier si ce type de notification est activé
    const settings = getSettings();
    const settingKey = {
      'hours_limit': 'hoursLimit',
      'cycle_limit': 'cycleLimit',
      'inspection': 'inspectionReminder',
      'message': 'messages'
    }[type];
    
    if (settingKey && !settings[settingKey]) {
      console.log('[Notifications] Type disabled by user:', type);
      return false;
    }

    try {
      // Utiliser le Service Worker si disponible
      if (swRegistration) {
        await swRegistration.showNotification(title, {
          body,
          icon: '/truck-icon.png',
          badge: '/truck-badge.png',
          tag: `propilote-${type}`,
          vibrate: [200, 100, 200],
          requireInteraction: options.requireInteraction || false,
          data: { url: options.url || '/' }
        });
      } else {
        // Fallback: notification directe
        new Notification(title, {
          body,
          icon: '/truck-icon.png',
          tag: `propilote-${type}`
        });
      }
      
      markNotificationSent(type);
      console.log('[Notifications] Sent:', title);
      return true;
    } catch (err) {
      console.error('[Notifications] Failed to send:', err);
      return false;
    }
  }, [permission, swRegistration, canSendNotification, getSettings, markNotificationSent]);

  // Vérifier les heures et envoyer des alertes si nécessaire
  const checkHoursAndNotify = useCallback((hoursData) => {
    if (permission !== 'granted') return;
    
    const settings = getSettings();
    const { drivingMin, totalWorkMin, cycleRemainingMin } = hoursData;
    
    // Alerte limite journalière de conduite (90%)
    if (settings.hoursLimit && drivingMin >= LIMITS.maxDriving * LIMITS.dailyWarningPercent) {
      const remaining = LIMITS.maxDriving - drivingMin;
      const remainingFormatted = `${Math.floor(remaining / 60)}h${remaining % 60 > 0 ? String(remaining % 60).padStart(2, '0') : ''}`;
      
      sendNotification(
        '⚠️ Limite de conduite proche',
        `Il vous reste ${remainingFormatted} de conduite aujourd'hui. Planifiez votre repos.`,
        { type: 'hours_limit', url: '/hours', requireInteraction: true }
      );
    }
    
    // Alerte limite journalière de service (90%)
    if (settings.hoursLimit && totalWorkMin >= LIMITS.maxOnDuty * LIMITS.dailyWarningPercent) {
      const remaining = LIMITS.maxOnDuty - totalWorkMin;
      const remainingFormatted = `${Math.floor(remaining / 60)}h${remaining % 60 > 0 ? String(remaining % 60).padStart(2, '0') : ''}`;
      
      sendNotification(
        '⚠️ Limite de service proche',
        `Il vous reste ${remainingFormatted} de service aujourd'hui. Planifiez votre repos.`,
        { type: 'hours_limit', url: '/hours', requireInteraction: true }
      );
    }
    
    // Alerte limite de cycle (<10h restantes)
    if (settings.cycleLimit && cycleRemainingMin !== undefined && cycleRemainingMin < LIMITS.cycleWarningThreshold) {
      const remainingHours = Math.floor(cycleRemainingMin / 60);
      
      sendNotification(
        '🚨 Limite de cycle critique!',
        `Il vous reste seulement ${remainingHours}h sur votre cycle. Prévoyez une période de repos prolongée.`,
        { type: 'cycle_limit', url: '/hours', requireInteraction: true }
      );
    }
  }, [permission, getSettings, sendNotification]);

  // Envoyer un rappel d'inspection
  const sendInspectionReminder = useCallback(() => {
    const settings = getSettings();
    if (!settings.inspectionReminder) return;
    
    sendNotification(
      '🚛 Rappel d\'inspection',
      'N\'oubliez pas de compléter votre inspection pré-départ avant de prendre la route.',
      { type: 'inspection', url: '/inspection' }
    );
  }, [getSettings, sendNotification]);

  // Envoyer une notification de nouveau message
  const sendMessageNotification = useCallback((senderName) => {
    const settings = getSettings();
    if (!settings.messages) return;
    
    sendNotification(
      '💬 Nouveau message',
      `Vous avez reçu un message de ${senderName || 'Dispatch'}`,
      { type: 'message', url: '/messages' }
    );
  }, [getSettings, sendNotification]);

  return {
    permission,
    supported,
    requestPermission,
    sendNotification,
    checkHoursAndNotify,
    sendInspectionReminder,
    sendMessageNotification,
    getSettings
  };
};

export default useNotifications;
